import axios from "../axios";
import { buildAgreementListUpdatePayload } from "../../utils/serviceAgreementMassEdit";

export async function fetchServiceAgreements(controller) {
  return axios({
    method: "get",
    url: "/service/agreements",
    params: controller,
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      // Goal 2 pool A: never resolve Axios failures as success payloads.
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchServiceAgreement(id) {
  return axios.get(`/service/agreements/` + id);
}

export async function createServiceAgreement(payload) {
  return axios
    .post(`/service/agreements`, { agreement: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}
export async function updateServiceAgreement(payload) {
  return axios
    .put(`/service/agreements/${payload.id}`, { agreement: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function createReportServiceAgreement(payload) {
  return axios
    .post(`/service/agreements/${payload.id}/report`, { agreement: payload })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function createOrderServiceAgreement(payload) {
  return axios
    .post(`/service/agreements/${payload.id}/order`, { agreement: payload })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function fetchServiceAgreementSetup() {
  return axios
    .get(`/service/agreements/setup`)
    .then((response) => response.data)
    .catch((error) => {
      throw error.response?.data || error;
    });
}

export async function getPdfServiceAgreement(payload) {
  try {
    const response = await axios.get(`/service/agreements/${payload.id}/pdf`, {
      params: payload.params,
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    const blob = await requirePdfBlob(response.data);
    const disposition = response.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    const filename = match ? decodeURIComponent(match[1].replace(/"/g, "")) : "serviceavtale.pdf";
    return { blob, filename };
  } catch (error) {
    if (error?.code === "AGREEMENT_PDF_INVALID") throw error;
    const data = error?.response?.data;
    if (typeof Blob !== "undefined" && data instanceof Blob) {
      await requirePdfBlob(data);
    }
    throw error.response?.data || error;
  }
}

function readBlobAsText(blob) {
  if (typeof blob?.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function requirePdfBlob(data) {
  if (!(data instanceof Blob)) {
    const err = new Error("Invalid pdf");
    err.code = "AGREEMENT_PDF_INVALID";
    throw err;
  }
  const header = await readBlobAsText(data.slice(0, 4));
  if (header !== "%PDF") {
    let message = "Invalid pdf";
    try {
      const json = JSON.parse(await readBlobAsText(data));
      if (Array.isArray(json.errors) && json.errors.length) {
        message = json.errors.join(", ");
      }
    } catch (_) {
      /* HTML or empty error body */
    }
    const err = new Error(message);
    err.code = "AGREEMENT_PDF_INVALID";
    throw err;
  }
  return data;
}

export async function sendServiceAgreement(payload) {
  return axios.put(`/service/agreements/${payload.id}/send`, {
    agreement: payload,
  });
}

export async function deleteServiceAgreement(payload) {
  return axios
    .delete(`/service/agreements/${payload.id}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function reactivateServiceAgreement(payload) {
  return axios
    .put(`/service/agreements/${payload.id}/reactivate`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

/**
 * Mass-update service agreements.
 * Expected path: PUT /api/v1/service/agreements/list
 * Body: { ids: "1,2,3", agreement: { customer_id?, project_id?, agreement_type_id?, interval_id? } }
 * Must never call /service/reports/list.
 */
export async function listUpdateServiceAgreement(payload) {
  const built =
    payload?.agreement != null
      ? {
          ids: Array.isArray(payload.ids)
            ? payload.ids.map(String)
            : String(payload.ids || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
          agreement: payload.agreement,
        }
      : buildAgreementListUpdatePayload(payload);

  if (!built.ids?.length) {
    const err = new Error("empty_selection");
    err.code = "empty_selection";
    throw err;
  }
  if (!built.agreement || Object.keys(built.agreement).length === 0) {
    const err = new Error("no_fields");
    err.code = "no_fields";
    throw err;
  }

  return axios
    .put(`/service/agreements/list`, {
      ids: built.ids.join(","),
      agreement: built.agreement,
    })
    .then((response) => response.data)
    .catch((error) => {
      return Promise.reject(error);
    });
}
